import SockJS from 'sockjs-client'
import Stomp, {type Subscription} from 'stompjs'
import {Subject} from 'rxjs'
import {config} from "@/config";
import {getStoredToken} from "@shared/helpers/tokenizer.helper";
import {SystemApi} from "@/api";

export class WebSocketHelper {
  private stompClient: any | null = null
  private reconnectionDelay = 15000
  retryConnectionInterval: any

  public wsConnectSubject = new Subject()
  public wsDisconnectSubject = new Subject()

  inactiveStartTime = 0
  inactiveTimeThreshold: number = 5

  private workspaceId = ''

  constructor() {
    this.inactiveTimeThreshold = config("APP_DISCONNECT_THRESHOLD") || 5;

    const self = this
    document.addEventListener('visibilitychange', function (event) {
      try {
        self.checkInactivity()
      } catch (e) {
        console.error(e)
      }
    }, false)
  }

  private shouldTryToConnect() : boolean {
    const path = window.location.pathname;
    return !path.includes('/publish/') && !path.includes('/auth/');
  }

  private checkInactivity() {
    const inactiveStatus = document?.hidden

    if (inactiveStatus && this.inactiveStartTime === 0) {
      this.inactiveStartTime = Date.now()
    } else if (!inactiveStatus) {
      this.inactiveStartTime = 0
      if (this.shouldTryToConnect() && (!this.stompClient || !this.stompClient.connected)) {
        this.connect(this.workspaceId)
      }
    }

    if (this.inactiveStartTime !== 0 && (Date.now() - this.inactiveStartTime) > (this.inactiveTimeThreshold * 1000 * 60)) {
      this.disconnect()
    }
  }

  private getClient() {
    if (this.stompClient == null) {
      const accessToken = getStoredToken().accessToken
      const serverUrl = config("APP_WS_URL")
      const socket = new SockJS(serverUrl + '/wssession?access_token=' + accessToken, [], {
        // sessionId: () => { return this.sessionId; },
        timeout: 15000
      })

      this.stompClient = Stomp.over(socket)
      this.stompClient.heartbeat.outgoing = 0
      this.stompClient.heartbeat.incoming = 30000
      this.stompClient.debug = () => {
      }
    } else {
      //console.log(new Date() + ' - GetClient : Return existing')
    }
    return this.stompClient
  }

  public connect(workspaceId: string) {
    this.workspaceId = workspaceId

    SystemApi.helloSocket().then(() => {
      const stompClient = this.getClient()
      if (stompClient != null) {
        if (stompClient.connected !== true) {
          this.stompClient.connect(
              {'X-WS': this.workspaceId},
              (frame: any) => {
                this.wsConnectSubject.next()
                this.stopRetry()
              },
              (error: any) => {
                this.wsDisconnectSubject.next()
                this.stompClient = null
                this.startRetry()
              }
          )
        } else {
          this.stopRetry()
        }
      }
    })
  }

  private startRetry() {
    this.stopRetry()
    this.retryConnectionInterval = setInterval(() => {
      if(this.shouldTryToConnect()) {
        this.connect(this.workspaceId)
      }
    }, this.reconnectionDelay)
  }

  private stopRetry() {
    if (this.retryConnectionInterval != null) {
      clearInterval(this.retryConnectionInterval)
    }
  }

  public subscribe(
      destination: string,
      callback: (message: any) => void
  ): Subscription | undefined {
    if (!this.stompClient || !this.stompClient.connected) {
      console.warn(`WebSocket is not connected. Unable to subscribe to ${destination}`);
      return undefined;
    }
    return this.stompClient.subscribe(destination, (message: any) => {
      callback(message);
    });
  }

  public sendMessage(destination: string, body: any): void {
    if (this.stompClient) {
      this.stompClient.send(destination, {}, JSON.stringify(body))
    }
  }

  public disconnect() {
    if (this.stompClient) {
      this.stompClient.disconnect(() => {
        this.stompClient = null
        this.wsDisconnectSubject.next()
      })
    }
  }
}
